import type { MouseEvent, RefObject } from 'react';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { Direction } from 're-resizable/lib/resizer';
import { useTranslation } from 'react-i18next';

import type { RootState } from '../../grid/reduxStore/Store';
import { useAppDispatch } from '../../grid/reduxStore/Store';
import { useSignaturesContext } from '../../providers/SignaturesProvider';
import {
  calculatePageMaxHeight,
  getBoundingPosition,
  getSnapPosition,
  roundToNearestMultipleOfGridSize,
  setupTrackedBlocks,
} from '../gridHelper';
import {
  setMaxHeightPage,
  updateCurrentBlockForGuideLines,
  updateGridDimensionConfig,
  updateGridPositionConfig,
} from '../../grid/reduxStore/editorSlice';
import type { BlockConfig } from '../models/BlockConfig.model';
import { openNotification } from '../../../notification';
import { useUndoRedoPositionChangeHandler } from '../../hooks/UseBlockPositionChangedHandler';
import { useUndoRedoDimensionChangedHandler } from '../../hooks/UseBlockDimensionChangedHandler';
import {
  useNewBlockDimensionChangedHandler,
  useNewBlockDimensionChangedHandlerWithoutUndoRedo,
} from '../../hooks/useNewBlockDimensionChangedHandler.ts';
import {
  useNewBlockPositionChangedHandler,
  useNewBlockPositionChangedHandlerWithoutUndoRedo,
} from '../../hooks/useNewBlockPositionChangedHandler';

import { debounce } from '../../../../utils/debounce';

import type {
  BlockDimensionType,
  BlockPositionType,
  HandleOnDragStartType,
  HandleOnDragType,
  HandleOnResizingStopType,
  HandleOnResizingType,
  ResizableDelta,
  UndoRedoTableContent,
} from './types';

import { GridBlockType } from 'services/entities/BlockTypes';
import { useSectionData } from '../../Sections/useSectionContext';
import { useBlockStyleSettings } from '../../hooks/useBlockSettings';
import { useSelector } from 'react-redux';
import { DocumentSettingsContext } from 'components/editor/SidePanel/DocumentSettings/DocumentDesignSettings/DocumentSettingsContext';
import type { PositionStateType } from '../../hooks/UseBlockPositionChangedHandler';
import type { DimensionStateChangedType, DimensionPositionStateChangedType } from '../../hooks/UseBlockDimensionChangedHandler';
import type { TableType } from '../../grid/reduxStore/table.types.ts';

type PositionDraggingProps = {
  x: number;
  y: number;
};

export const useDraggableBlockManipulations = () => {
  const { blocksContent, sectionId } = useSectionData();
  const blockNewPositionChangedHandlerWithoutUndoRedo = useNewBlockPositionChangedHandlerWithoutUndoRedo();
  const blockNewPositionChangedHandler = useNewBlockPositionChangedHandler();
  const { documentSettings, effectiveCanvasWidth } = useContext(DocumentSettingsContext);
  const { getBlockStyle } = useBlockStyleSettings();
  const { blockStyle } = useSelector((state: RootState) => state.blockStyleSettings) || {};
  const { getSignaturesBySectionId, handleSignaturePositionUpdate, handleSignatureDimensionUpdate, handleSignaturePropertyUpdate } =
    useSignaturesContext();
  const blockNewDimensionChangedHandler = useNewBlockDimensionChangedHandler();
  const blockNewDimensionChangedHandlerWithoutUndoRedo = useNewBlockDimensionChangedHandlerWithoutUndoRedo();

  const signatures = getSignaturesBySectionId(sectionId);
  const blocksContentRef = useRef(blocksContent);

  useEffect(() => {
    blocksContentRef.current = blocksContent;
  }, [blocksContent]);

  const trackedBlocks = setupTrackedBlocks(blocksContent, signatures);

  const signaturePositionChangedHandlerWithoutUndoRedo = useCallback(
    async (sectionId: string, blockId: string, blockState: PositionStateType) => {
      const block = blocksContentRef.current[blockId];
      if (!block) return;

      handleSignaturePropertyUpdate(sectionId, {
        signatureBoxId: blockId,
        properties: {
          position: { x: blockState.xAxisPx, y: blockState.yAxisPx },
          dimensions: { width: block.blockConfig.width, height: block.blockConfig.height },
        },
      });
    },
    [handleSignaturePropertyUpdate]
  );

  const pushSignaturePositionUndoRedo = useUndoRedoPositionChangeHandler(signaturePositionChangedHandlerWithoutUndoRedo);
  const signaturePositionChangedHandler = useCallback(
    async (sectionId: string, blockId: string, positionBefore: PositionStateType, positionAfter: PositionStateType) => {
      await signaturePositionChangedHandlerWithoutUndoRedo(sectionId, blockId, positionAfter);
      pushSignaturePositionUndoRedo(sectionId, blockId, positionBefore, positionAfter);
    },
    [signaturePositionChangedHandlerWithoutUndoRedo, pushSignaturePositionUndoRedo]
  );

  const signatureDimensionChangedHandlerWithoutUndoRedo = useCallback(
    async (sectionId: string, blockId: string, blockState: DimensionStateChangedType) => {
      const block = blocksContentRef.current[blockId];
      if (!block) return;

      handleSignaturePropertyUpdate(sectionId, {
        signatureBoxId: blockId,
        properties: {
          position: { x: block.blockConfig.x, y: block.blockConfig.y },
          dimensions: { width: blockState.widthPx, height: blockState.heightPx },
        },
      });
    },
    [handleSignaturePropertyUpdate]
  );

  const pushSignatureDimensionUndoRedo = useUndoRedoDimensionChangedHandler(
    signatureDimensionChangedHandlerWithoutUndoRedo,
    signaturePositionChangedHandlerWithoutUndoRedo
  );

  const signatureDimensionChangedHandler = useCallback(
    async (
      sectionId: string,
      blockId: string,
      stateBeforeResizing: DimensionPositionStateChangedType,
      stateAfterResizing: DimensionPositionStateChangedType
    ) => {
      await signatureDimensionChangedHandlerWithoutUndoRedo(sectionId, blockId, {
        widthPx: stateAfterResizing.widthPx,
        heightPx: stateAfterResizing.heightPx,
      });
      pushSignatureDimensionUndoRedo(sectionId, blockId, stateBeforeResizing, stateAfterResizing);
    },
    [signatureDimensionChangedHandlerWithoutUndoRedo, pushSignatureDimensionUndoRedo]
  );

  const calculatedMaxHeight = calculatePageMaxHeight(blocksContent, signatures);

  const [maxPageHeight, setMaxPageHeight] = useState<number>(calculatedMaxHeight);
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const mouseMovementWhenDragging = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  const showOutsideCanvasWarning = useMemo(
    () =>
      debounce(() => {
        openNotification({
          title: t('editor.blocks.placing.outside_canvas_warn.title'),
          description: t('editor.blocks.placing.outside_canvas_warn.desc'),
          type: 'warning',
        });
      }, 300),
    [t]
  );

  const resetManipulatedBlockForGuidelines = () => {
    const payload = { currentManipulatedBlock: null, sectionId: sectionId };
    dispatch(updateCurrentBlockForGuideLines(payload));
  };

  const calculateNewPositionBasedOnResizing = (direction: Direction, delta: ResizableDelta, position: BlockPositionType) => {
    const newPosition = { xAxisPx: position.xAxisPx, yAxisPx: position.yAxisPx };
    const left = -delta.width;
    const top = -delta.height;

    if (direction === 'bottomLeft') {
      newPosition.xAxisPx += left;
    } else if (direction === 'topRight') {
      newPosition.yAxisPx += top;
    } else if (['top', 'left', 'topLeft'].includes(direction)) {
      newPosition.xAxisPx += left;
      newPosition.yAxisPx += top;
    }
    return newPosition;
  };

  const getSelectedBlockWithUpdatedDimensions = (delta: ResizableDelta, selectedBlockConfig: BlockConfig): BlockConfig => {
    const selectedBlockWithUpdatedDimensions = { ...selectedBlockConfig };
    selectedBlockWithUpdatedDimensions.width += delta.width;
    selectedBlockWithUpdatedDimensions.height += delta.height;
    return selectedBlockWithUpdatedDimensions;
  };

  const setDocumentHeight = useCallback(
    (currentBlock?) => {
      const computedPageMaxHeightFromAllBlocks = calculatePageMaxHeight(blocksContent, signatures, currentBlock);
      setMaxPageHeight(computedPageMaxHeightFromAllBlocks);
    },

    [blocksContent, signatures]
  );

  const calculateMouseMovementAndPosition = (
    event: MouseEvent,
    draggedBlock: BlockConfig,
    mouseMovementWhenDragging: RefObject<{ x: number; y: number }>
  ) => {
    if (!mouseMovementWhenDragging.current) return;
    mouseMovementWhenDragging.current.x += event.movementX;
    mouseMovementWhenDragging.current.y += event.movementY;

    return {
      x: mouseMovementWhenDragging.current.x + draggedBlock.x,
      y: mouseMovementWhenDragging.current.y + draggedBlock.y,
    };
  };

  const calculateNewPosition = (
    snapPosition: { x: number | null; y: number | null },
    boundingPosition: { x: number | null; y: number | null },
    previousPosition: { x: number; y: number },
    mouseMovementWhenDragging: RefObject<{ x: number; y: number }>
  ) => {
    if (!mouseMovementWhenDragging.current) return;
    mouseMovementWhenDragging.current.x = 0;
    mouseMovementWhenDragging.current.y = 0;

    const newX = boundingPosition.x ?? snapPosition.x ?? previousPosition.x;
    const newY = boundingPosition.y ?? snapPosition.y ?? previousPosition.y;

    return { x: newX, y: newY };
  };

  const handleDrag: HandleOnDragType = (event, _data, draggedBlock, blockType, elementToBound) => {
    const payload = { currentManipulatedBlock: draggedBlock, sectionId: sectionId };
    dispatch(updateCurrentBlockForGuideLines(payload));

    const positionWhenDragging: PositionDraggingProps = calculateMouseMovementAndPosition(
      event,
      draggedBlock,
      mouseMovementWhenDragging
    ) as PositionDraggingProps;

    // Use throttled version during drag to improve performance
    setDocumentHeight(draggedBlock);

    const boundaries = elementToBound ? { height: elementToBound.clientHeight, width: elementToBound.clientWidth } : undefined;

    const boundingPosition = getBoundingPosition({ ...draggedBlock, ...positionWhenDragging }, boundaries);

    const snapPosition = getSnapPosition(
      { ...draggedBlock, ...positionWhenDragging },
      trackedBlocks,
      effectiveCanvasWidth,
      documentSettings.margin
    );

    const newPosition = calculateNewPosition(
      snapPosition,
      boundingPosition,
      positionWhenDragging,
      mouseMovementWhenDragging
    ) as PositionDraggingProps;

    if (blockType === GridBlockType.SIGNATURE) {
      handleSignaturePositionUpdate(sectionId, draggedBlock.id, { ...newPosition });
    } else {
      dispatch(
        updateGridPositionConfig({
          sectionId: sectionId,
          blockId: draggedBlock.id,
          ...newPosition,
        })
      );
    }
  };

  const handleSignatureKeyboardMove = ({ blockConfig }: { blockConfig: BlockConfig }) => {
    if (!blockConfig) return;
    const { id } = blockConfig;
    const payload = { currentManipulatedBlock: blockConfig, sectionId: sectionId };
    dispatch(updateCurrentBlockForGuideLines(payload));
    dispatch(updateGridPositionConfig({ sectionId: sectionId, blockId: id, x: blockConfig.x, y: blockConfig.y }));
    handleSignaturePositionUpdate(sectionId, blockConfig.id, { x: blockConfig.x, y: blockConfig.y });
    setDocumentHeight(blockConfig);
    setTimeout(() => {
      resetManipulatedBlockForGuidelines();
    }, 300);
  };

  const handleKeyboardMove = async ({ blockConfig, deltaX, deltaY }: { blockConfig: BlockConfig; deltaX: number; deltaY: number }) => {
    if (!blockConfig) return;
    const { id } = blockConfig;
    const currentX = blockConfig?.x || 0;
    const currentY = blockConfig?.y || 0;

    const newX = currentX + deltaX;
    const newY = currentY + deltaY;

    const blockWithNewPosition = { ...blockConfig, x: newX, y: newY };
    const payload = { currentManipulatedBlock: blockWithNewPosition, sectionId: sectionId };
    dispatch(updateCurrentBlockForGuideLines(payload));
    dispatch(updateGridPositionConfig({ sectionId: sectionId, blockId: id, x: newX, y: newY }));

    const positionOnDragStart = { xAxisPx: currentX, yAxisPx: currentY };
    const updatedBlock = {
      blockConfig,
      ...blockConfig,
      x: newX,
      y: newY,
      id,
    };

    await handleStop({} as MouseEvent, positionOnDragStart, updatedBlock, sectionId);
  };

  const handleDragStart: HandleOnDragStartType = () => {
    mouseMovementWhenDragging.current = { x: 0, y: 0 };
  };

  const isBlockDroppedOffCanvas = ({ x, y, width, height }: { x: number; y: number; width: number; height: number }) => {
    const isMovedOffRightSide = x >= effectiveCanvasWidth;
    const isMovedOffLeftSide = x + width <= 0;
    const isMovedOffTopSide = y + height <= 0;

    return isMovedOffRightSide || isMovedOffLeftSide || isMovedOffTopSide;
  };

  const hasPositionChangedOnDrag = (
    leftPxOnDragStart: number,
    leftPxOnDragStop: number,
    topPxOnDragStart: number,
    topPxOnDragStop: number
  ): boolean => leftPxOnDragStart !== leftPxOnDragStop || topPxOnDragStart !== topPxOnDragStop;

  const handleStop = async (
    _event: MouseEvent,
    positionOnDragStart: BlockPositionType,
    item: BlockConfig,
    sectionId: string,
    blockType?: GridBlockType
  ) => {
    setDocumentHeight(item);

    if (!hasPositionChangedOnDrag(positionOnDragStart.xAxisPx, item.x, positionOnDragStart.yAxisPx, item.y)) return;

    const positionBefore = { xAxisPx: positionOnDragStart.xAxisPx, yAxisPx: positionOnDragStart.yAxisPx };
    if (isBlockDroppedOffCanvas(item)) {
      if (blockType === GridBlockType.SIGNATURE) {
        await signaturePositionChangedHandlerWithoutUndoRedo(sectionId, item.id, positionBefore);
      } else {
        await blockNewPositionChangedHandlerWithoutUndoRedo(sectionId, item.id, positionBefore);
      }
      showOutsideCanvasWarning();

      const computedPageMaxHeightFromAllBlocks = calculatePageMaxHeight(blocksContent, signatures);
      if (maxPageHeight === computedPageMaxHeightFromAllBlocks) {
        setMaxPageHeight(computedPageMaxHeightFromAllBlocks);
      }
      return;
    }

    const positionAfter = { xAxisPx: roundToNearestMultipleOfGridSize(item.x), yAxisPx: roundToNearestMultipleOfGridSize(item.y) };
    if (blockType === GridBlockType.SIGNATURE) {
      const updatedBlocksContent = { ...blocksContentRef.current };
      if (updatedBlocksContent[item.id]) {
        updatedBlocksContent[item.id] = {
          ...updatedBlocksContent[item.id],
          blockConfig: {
            ...updatedBlocksContent[item.id].blockConfig,
            x: positionAfter.xAxisPx,
            y: positionAfter.yAxisPx,
            width: item.width,
            height: item.height,
          },
        };
        blocksContentRef.current = updatedBlocksContent;
      }
      await signaturePositionChangedHandler(sectionId, item.id, positionBefore, positionAfter);
    } else {
      await blockNewPositionChangedHandler(sectionId, item.id, positionBefore, positionAfter);
    }
    resetManipulatedBlockForGuidelines();
  };

  const hasDimensionsChangedOnResize = (delta: { width: number; height: number }): boolean => delta.width !== 0 || delta.height !== 0;

  const getAdjustBlockHeight = (blockStateOnResizeStop: BlockConfig) => {
    const { borderBottom = 0, borderTop = 0, paddingBottom = 0, paddingTop = 0 } = blockStyle[blockStateOnResizeStop.id] || getBlockStyle();

    const sumBoxModelHeight = borderBottom + borderTop + paddingBottom + paddingTop;

    const tiptapEditorSelector = `.tiptap_editor_${blockStateOnResizeStop.id} .tiptap`;
    const tiptapEditorHeight = document.querySelector(tiptapEditorSelector)?.getBoundingClientRect().height || 0;
    const totalTiptapHeight = tiptapEditorHeight + sumBoxModelHeight;

    return Math.max(totalTiptapHeight, blockStateOnResizeStop.height);
  };

  const handleOnResizeStop: HandleOnResizingStopType = async (
    _direction,
    delta,
    blockStateOnResizeStop,
    positionOnResizeStart: BlockPositionType,
    dimensionOnResizeStart: BlockDimensionType,
    _sumBorderWidth?: number,
    blockType?: GridBlockType,
    onBlockResizeStop?: (newWidth: number) => UndoRedoTableContent | undefined
  ) => {
    resetManipulatedBlockForGuidelines();

    if (!hasDimensionsChangedOnResize(delta)) return;
    const stateBeforeResizing = {
      widthPx: dimensionOnResizeStart.widthPx,
      heightPx: dimensionOnResizeStart.heightPx,
      yAxisPx: positionOnResizeStart.yAxisPx,
      xAxisPx: positionOnResizeStart.xAxisPx,
    };

    const adjustedBlockStateOnResizeStopHeight = getAdjustBlockHeight(blockStateOnResizeStop);
    const stateAfterResizing = {
      widthPx: blockStateOnResizeStop.width,
      heightPx: adjustedBlockStateOnResizeStopHeight,
      yAxisPx: blockStateOnResizeStop.y,
      xAxisPx: blockStateOnResizeStop.x,
    };

    if (
      isBlockDroppedOffCanvas({
        x: stateAfterResizing.xAxisPx,
        y: stateAfterResizing.yAxisPx,
        height: stateAfterResizing.heightPx,
        width: stateAfterResizing.widthPx,
      })
    ) {
      showOutsideCanvasWarning();

      if (blockType === GridBlockType.SIGNATURE) {
        await signaturePositionChangedHandlerWithoutUndoRedo(sectionId, blockStateOnResizeStop.id, stateBeforeResizing);
        await signatureDimensionChangedHandlerWithoutUndoRedo(sectionId, blockStateOnResizeStop.id, stateBeforeResizing);
      } else {
        await blockNewPositionChangedHandlerWithoutUndoRedo(sectionId, blockStateOnResizeStop.id, stateBeforeResizing);
        await blockNewDimensionChangedHandlerWithoutUndoRedo(sectionId, blockStateOnResizeStop.id, stateBeforeResizing);
      }

      const computedPageMaxHeightFromAllBlocks = calculatePageMaxHeight(blocksContent, signatures);
      if (maxPageHeight === computedPageMaxHeightFromAllBlocks) {
        setMaxPageHeight(computedPageMaxHeightFromAllBlocks);
      }
      return;
    }

    if (blockType === GridBlockType.SIGNATURE) {
      const updatedBlocksContent = { ...blocksContentRef.current };
      if (updatedBlocksContent[blockStateOnResizeStop.id]) {
        updatedBlocksContent[blockStateOnResizeStop.id] = {
          ...updatedBlocksContent[blockStateOnResizeStop.id],
          blockConfig: {
            ...updatedBlocksContent[blockStateOnResizeStop.id].blockConfig,
            x: stateAfterResizing.xAxisPx,
            y: stateAfterResizing.yAxisPx,
            width: stateAfterResizing.widthPx,
            height: stateAfterResizing.heightPx,
          },
        };
        blocksContentRef.current = updatedBlocksContent;
      }
      await signatureDimensionChangedHandler(sectionId, blockStateOnResizeStop.id, stateBeforeResizing, stateAfterResizing);
    } else {
      let tableContent: { previousContent: TableType; newContent: TableType } | undefined;

      if (onBlockResizeStop) {
        const result = onBlockResizeStop(blockStateOnResizeStop.width);
        if (result) {
          tableContent = result;
        }
      }

      await blockNewDimensionChangedHandler(sectionId, blockStateOnResizeStop.id, stateBeforeResizing, stateAfterResizing, tableContent);
    }
  };

  const handleOnResizing: HandleOnResizingType = (
    direction: Direction,
    delta: ResizableDelta,
    positionOnInteractionStart: BlockPositionType,
    dimensionOnInteractionStart: BlockDimensionType,
    selectedBlock: BlockConfig,
    blockType: GridBlockType
  ) => {
    const payload = { currentManipulatedBlock: selectedBlock, sectionId: sectionId };
    dispatch(updateCurrentBlockForGuideLines(payload));

    const newPosition = calculateNewPositionBasedOnResizing(direction, delta, positionOnInteractionStart);
    const updatedBlock = {
      ...selectedBlock,
      x: newPosition.xAxisPx,
      y: newPosition.yAxisPx,
      height: dimensionOnInteractionStart.heightPx + delta.height,
      width: dimensionOnInteractionStart.widthPx + delta.width,
    };

    if (blockType === GridBlockType.SIGNATURE) {
      handleSignaturePositionUpdate(sectionId, updatedBlock.id, { x: updatedBlock.x, y: updatedBlock.y });
      handleSignatureDimensionUpdate(sectionId, updatedBlock.id, { height: updatedBlock.height, width: updatedBlock.width });
    } else {
      dispatch(updateGridPositionConfig({ sectionId: sectionId, blockId: updatedBlock.id, x: updatedBlock.x, y: updatedBlock.y }));
      dispatch(
        updateGridDimensionConfig({
          sectionId: sectionId,
          blockId: updatedBlock.id,
          width: updatedBlock.width,
          height: updatedBlock.height,
        })
      );
    }

    setDocumentHeight(getSelectedBlockWithUpdatedDimensions(delta, updatedBlock));
  };

  useEffect(() => {
    setDocumentHeight();
  }, [blocksContent]);

  useEffect(() => {
    const payload = { maxHeightPage: maxPageHeight, sectionId: sectionId };
    dispatch(setMaxHeightPage(payload));
  }, [maxPageHeight]);

  return {
    handleOnResizing,
    handleDrag,
    handleDragStart,
    handleOnResizeStop,
    handleStop,
    setDocumentHeight,
    resetManipulatedBlockForGuidelines,
    getAdjustBlockHeight,
    handleKeyboardMove,
    handleSignatureKeyboardMove,
  };
};
